import React, { useState, useEffect, useRef } from "react";
import {
  IconButton,
  Tooltip,
  makeStyles,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import clsx from "clsx";
import RippleEffect from "../RippleEffect";

// 🎯 Estilos avançados com micro-animações
const useStyles = makeStyles((theme) => ({
  // 🎭 Keyframes para animações
  "@keyframes shine": {
    "0%": {
      transform: "translateX(-100%)",
    },
    "100%": {
      transform: "translateX(100%)",
    },
  },
  
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
    "50%": {
      transform: "scale(1.05)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    },
    "100%": {
      transform: "scale(1)",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
  },

  "@keyframes bounceIn": {
    "0%": {
      opacity: 0,
      transform: "scale(0.3)",
    },
    "50%": {
      opacity: 1,
      transform: "scale(1.05)",
    },
    "70%": {
      transform: "scale(0.9)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1)",
    },
  },

  // 🎯 Container principal do botão
  messageActionButton: {
    width: "32px",
    height: "32px",
    padding: 0,
    borderRadius: "50%",
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    willChange: "transform, box-shadow, background-color",
    
    // 🎨 Estados de cor - Default (branco com blur)
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    color: "#54656f",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",

    // 🎯 Hover - Elevação e transformação
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 1)",
      transform: "scale(1.1) translateY(-1px)",
      boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
      color: "#111b21",
      
      // Efeito de brilho
      "&::before": {
        opacity: 1,
        animation: "$shine 0.6s ease-in-out",
      },
    },

    // 🎯 Active - Feedback tátil
    "&:active": {
      transform: "scale(0.95)",
      transition: "all 0.1s ease",
      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
    },

    // 🎯 Focus - Acessibilidade
    "&:focus": {
      outline: "2px solid #00a884",
      outlineOffset: "2px",
    },

    // 💫 Pseudo-elemento para efeito de brilho
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)",
      opacity: 0,
      transition: "opacity 0.3s ease",
      pointerEvents: "none",
    },

    // 🎯 Ícone interno
    "& .MuiSvgIcon-root": {
      fontSize: "18px",
      transition: "all 0.3s ease",
    },

    // 📱 Responsividade
    [theme.breakpoints.down("xs")]: {
      width: "28px",
      height: "28px",
      "& .MuiSvgIcon-root": {
        fontSize: "16px",
      },
    },
  },

  // 🎨 Variante Primary (Verde WhatsApp)
  primary: {
    backgroundColor: "#00a884",
    color: "white",
    border: "1px solid #00a884",
    
    "&:hover": {
      backgroundColor: "#008c6f",
      color: "white",
      boxShadow: "0 8px 16px rgba(0, 168, 132, 0.3)",
    },
    
    "&:active": {
      backgroundColor: "#007a63",
    },
  },

  // 🎨 Variante Success (Verde sucesso)
  success: {
    backgroundColor: "#10b981",
    color: "white",
    border: "1px solid #10b981",
    
    "&:hover": {
      backgroundColor: "#059669",
      color: "white",
      boxShadow: "0 8px 16px rgba(16, 185, 129, 0.3)",
    },
    
    "&:active": {
      backgroundColor: "#047857",
    },
  },

  // 🎨 Variante Danger (Vermelho erro)
  danger: {
    backgroundColor: "#ef4444",
    color: "white",
    border: "1px solid #ef4444",
    
    "&:hover": {
      backgroundColor: "#dc2626",
      color: "white",
      boxShadow: "0 8px 16px rgba(239, 68, 68, 0.3)",
    },
    
    "&:active": {
      backgroundColor: "#b91c1c",
    },
  },

  // 🎨 Variante Warning (Amarelo aviso)
  warning: {
    backgroundColor: "#f59e0b",
    color: "white",
    border: "1px solid #f59e0b",
    
    "&:hover": {
      backgroundColor: "#d97706",
      color: "white",
      boxShadow: "0 8px 16px rgba(245, 158, 11, 0.3)",
    },
    
    "&:active": {
      backgroundColor: "#b45309",
    },
  },

  // ⚡ Animação de pulsação para estados especiais
  pulse: {
    animation: "$pulse 2s infinite",
  },

  // 🎭 Animação de entrada
  bounceIn: {
    animation: "$bounceIn 0.6s ease-out",
  },

  // 🎯 Estados disabled
  disabled: {
    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
    color: "rgba(0, 0, 0, 0.26) !important",
    cursor: "not-allowed !important",
    transform: "none !important",
    boxShadow: "none !important",
    
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.12) !important",
      transform: "none !important",
      boxShadow: "none !important",
      
      "&::before": {
        opacity: "0 !important",
      },
    },
  },

  // 🎯 Tooltip customizado
  tooltip: {
    backgroundColor: "rgba(17, 27, 33, 0.9)",
    color: "#ffffff",
    fontSize: "12px",
    borderRadius: "6px",
    padding: "6px 12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    animation: "$bounceIn 0.3s ease-out",
  },

  // 🎯 Seta do tooltip
  arrow: {
    color: "rgba(17, 27, 33, 0.9)",
  },
}));

// 🎯 Componente MessageActionButton
const MessageActionButton = ({
  children,
  variant = "default",
  tooltip = "",
  pulse = false,
  animate = true,
  disabled = false,
  onClick,
  className,
  size = "medium",
  ripple = true,
  "aria-label": ariaLabel,
  ...rest
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [mounted, setMounted] = useState(!animate);
  const [rippleState, setRippleState] = useState(null);
  const buttonRef = useRef(null);

  // ✨ Animação de entrada
  useEffect(() => {
    if (animate && !mounted) {
      const timer = setTimeout(() => setMounted(true), 100);
      return () => clearTimeout(timer);
    }
  }, [animate, mounted]);

  // 🎯 Classes dinâmicas
  const buttonClasses = clsx(
    classes.messageActionButton,
    {
      [classes.primary]: variant === "primary",
      [classes.success]: variant === "success", 
      [classes.danger]: variant === "danger",
      [classes.warning]: variant === "warning",
      [classes.pulse]: pulse && !disabled,
      [classes.bounceIn]: animate && mounted,
      [classes.disabled]: disabled,
    },
    className
  );
  // 🎯 Handler de click com feedback
  const handleClick = (event) => {
    if (disabled) return;
    
    // Efeito ripple
    if (ripple && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      setRippleState({
        x,
        y,
        width: rect.width,
        height: rect.height,
      });
    }
    
    // Feedback háptico em mobile (se disponível)
    if (isMobile && navigator.vibrate) {
      navigator.vibrate(50);
    }
    
    if (onClick) {
      onClick(event);
    }
  };
  // 🎯 Componente do botão
  const ButtonComponent = (
    <IconButton
      ref={buttonRef}
      className={buttonClasses}
      onClick={handleClick}
      disabled={disabled}
      size={size}
      aria-label={ariaLabel || tooltip}
      {...rest}
    >
      {children}
      {ripple && <RippleEffect trigger={rippleState} />}
    </IconButton>
  );

  // 🎯 Com tooltip ou sem tooltip
  if (tooltip && !disabled) {
    return (
      <Tooltip
        title={tooltip}
        placement="top"
        classes={{
          tooltip: classes.tooltip,
          arrow: classes.arrow,
        }}
        arrow
        enterDelay={500}
        leaveDelay={200}
      >
        {ButtonComponent}
      </Tooltip>
    );
  }

  return ButtonComponent;
};

export default MessageActionButton;
